import axios from 'axios';
import { useEffect, useState } from 'react';
import { Breadcrumb } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthState } from '../../services/auth';
import './device-details.scss';
import { DetailsDevices } from './details/DetailsDevices';
import { ManageDevices } from './manage/ManageDevices';
import { MetricsDevices } from './metrics/MetricsDevices';
import { ThreatsByType } from './threats/Threats';
import { DeviceActivity } from './deviceActivity/DeviceActivity';
import { MobileApps } from './mobile-apps/MobileApps';
import { CVEThreats } from './cveThreats/CVEThreats';
import RiskScoreCardGrid from './RiskScoreCardGrid/RiskScoreCardGrid';
import {
  Activity,
  ArrowLeft,
  Bug,
  Gauge,
  Globe,
  Info,
  LucideIcon,
  MonitorSmartphone,
  ShieldAlert,
  ShieldCheck,
  ShieldOff,
  SlidersHorizontal,
  Smartphone,
  Wifi,
  WifiOff,
} from 'lucide-react';
import '../../design-system/index.css';

type Props = {
  accountsPage?: boolean;
  devicesPage?: boolean;
  darkmode?: boolean;
};

// Type definitions - Move these outside the component
interface RiskScore {
  current: number;
  overall: number;
  overallChange: number;
  previousScores: number[];
  cvePercentage: number;
  dnsPercentage: number;
  phoneSmsPercentage: number;
}

interface DNSRecord {
  domain: string;
  category: 'normal' | 'malware' | 'scam' | 'phishing' | 'suspicious';
  likelihood: number;
  isSuspicious: boolean;
  timestamp: string;
  requestCount: number;
  threatLevel: 'low' | 'medium' | 'high' | 'critical';
}

interface DNSData {
  totalCount: number;
  suspiciousCount: number;
  categories: {
    normal: number;
    malware: number;
    scam: number;
    phishing: number;
    suspicious: number;
  };
  records: DNSRecord[];
  normalToSuspiciousRatio: {
    normal: number;
    suspicious: number;
  };
}

interface PhoneSMSRecord {
  number: string;
  threatType: 'scam' | 'phishing' | 'spam' | 'normal' | 'fraud';
  flagged: boolean;
  description?: string;
  timestamp: string;
  callDuration?: number;
  smsContent?: string;
  riskScore: number;
}

interface PhoneSMSData {
  totalCount: number;
  flaggedCount: number;
  records: PhoneSMSRecord[];
  threatBreakdown: {
    scam: number;
    phishing: number;
    spam: number;
    fraud: number;
  };
}

// Fix CVEData interface - make it an object, not an array
interface CVEData {
  totalCount: number;
  criticalCount: number;
  highCount: number;
  mediumCount: number;
  lowCount: number;
  riskScore: number;
  severityDistribution: {
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
}

interface RiskDescription {
  hasIssues: boolean;
  pointsIncrease: number;
  causes: string[];
  suggestions: string[];
  riskLevel: 'low' | 'medium' | 'high' | 'critical';
  timestamp: string;
}

interface DeviceRiskScore {
  deviceName: string;
  deviceType: string;
  deviceId: string;
  scoreLevel: 'excellent' | 'good' | 'fair' | 'poor' | 'critical';
  riskScore: RiskScore;
  cveData: CVEData; // Changed from CVEData[] to CVEData
  dnsData: DNSData;
  phoneSMSData: PhoneSMSData;
  description: RiskDescription;
  lastUpdated: string;
}

// Sample DNS records
const sampleDNSRecords: DNSRecord[] = [
  {
    domain: "example.com",
    category: "normal",
    likelihood: 10,
    isSuspicious: false,
    timestamp: "2023-12-01T10:00:00Z",
    requestCount: 5,
    threatLevel: "low"
  },
  {
    domain: "malicious-site.com",
    category: "malware",
    likelihood: 85,
    isSuspicious: true,
    timestamp: "2023-12-01T11:30:00Z",
    requestCount: 2,
    threatLevel: "high"
  }
];

// Sample Phone/SMS records
const samplePhoneRecords: PhoneSMSRecord[] = [
  {
    number: "+1234567890",
    threatType: "scam",
    flagged: true,
    description: "Potential scam call",
    timestamp: "2023-12-01T09:15:00Z",
    callDuration: 45,
    riskScore: 75
  },
  {
    number: "+0987654321",
    threatType: "normal",
    flagged: false,
    timestamp: "2023-12-01T14:20:00Z",
    callDuration: 120,
    riskScore: 10
  }
];

// Sample device data with CVE data
const sampleDeviceData: DeviceRiskScore = {
  deviceName: "Sample Device",
  deviceType: "Smartphone",
  deviceId: "DEV-12345",
  scoreLevel: "fair",
  riskScore: {
    current: 65,
    overall: 72,
    overallChange: -5,
    previousScores: [70, 68, 72, 75, 77],
    cvePercentage: 45,
    dnsPercentage: 25,
    phoneSmsPercentage: 15
  },
  dnsData: {
    totalCount: 1250,
    suspiciousCount: 45,
    categories: {
      normal: 1205,
      malware: 15,
      scam: 20,
      phishing: 8,
      suspicious: 2
    },
    records: sampleDNSRecords, // Use the actual array
    normalToSuspiciousRatio: {
      normal: 96.4,
      suspicious: 3.6
    }
  },
  phoneSMSData: {
    totalCount: 150,
    flaggedCount: 8,
    records: samplePhoneRecords, // Use the actual array
    threatBreakdown: {
      scam: 4,
      phishing: 2,
      spam: 1,
      fraud: 1
    }
  },
  // Add CVE Data
  cveData: {
    totalCount: 12,
    criticalCount: 2,
    highCount: 4,
    mediumCount: 5,
    lowCount: 1,
    riskScore: 65,
    severityDistribution: {
      critical: 16.7,
      high: 33.3,
      medium: 41.7,
      low: 8.3
    }
  },
  description: {
    hasIssues: true,
    pointsIncrease: 15,
    causes: [
      "Multiple suspicious DNS queries detected",
      "High-risk application permissions",
      "Unusual communication patterns"
    ],
    suggestions: [
      "Review and update application permissions",
      "Monitor network traffic for anomalies",
      "Run security scan on device"
    ],
    riskLevel: "medium",
    timestamp: "2023-12-01T10:30:00Z"
  },
  lastUpdated: "2023-12-01T10:30:00Z"
};

// Function to get sample device data based on device type
const getSampleDeviceData = (deviceType: string): DeviceRiskScore => {
  const baseData = { ...sampleDeviceData };
  
  // Modify data based on device type
  switch (deviceType) {
    case 'mobile':
      baseData.deviceType = 'Smartphone';
      baseData.riskScore.cvePercentage = 55; // Higher risk for mobile
      break;
    case 'server':
      baseData.deviceType = 'Server';
      baseData.riskScore.cvePercentage = 35; // Lower risk for server
      break;
    case 'iot':
      baseData.deviceType = 'IoT Device';
      baseData.riskScore.cvePercentage = 70; // Highest risk for IoT
      break;
    default:
      baseData.deviceType = 'Laptop';
      baseData.riskScore.cvePercentage = 45;
  }
  
  return baseData;
};

export const DeviceDetails = (props: Props) => {
  const navigate = useNavigate();
  const { operatorId, cpeId, deviceId, type } = useParams();
  const { token } = useAuthState();
  const { t } = useTranslation();

  const [summaryData, setSummaryData] = useState<any>(true);
  const [selectedMenu, setSelectedMenu] = useState('Details');
  const [devicesData, setDevicesData] = useState<any>();
  // const [devicesRiskReport, setDevicesRiskReport] = useState<any>();
  const [isActivity, setIsActivity] = useState<boolean>(false);
  // Derived at render so the label follows the active language
  const backBtnText =
    type === 'accountsPage'
      ? t('deviceDetails.page.backToCpe', { cpeId })
      : type === 'devicesPage'
        ? t('deviceDetails.page.backToDevices')
        : '';

  useEffect(() => {
    if (token) {
      fetchDeviceInfo();
      // fetchDeviceRiskReport();
      fetchRetinaDnsVisibility();
    }
  }, [token]);

  // Fetch device details
  async function fetchDeviceInfo() {
    try {
      const response = await axios.get(`/v2/op/device/${deviceId}/device-info`);
      setDevicesData(response.data);
    } catch (error) {
      console.error('Error fetching device info:', error);
    }
  }
  // Fetch device details
  // async function fetchDeviceRiskReport() {
  //   try {
  //     const response = await axios.get(`/v2/op/device/${deviceId}/device-risk-report`);
  //     setDevicesRiskReport(response.data);
  //     console.log('Device Risk Report:', response.data);
  //   } catch (error) {
  //     console.error('Error fetching device risk:', error);
  //   }
  // }

  // Fetch Retina DNS visibility (controls Activity tab)
  async function fetchRetinaDnsVisibility() {
    try {
      const response = await axios.get(
        `/v2/op/master-operator/${operatorId}/fetch-retina-dns-visibility`
      );

      if (response?.data?.retinaDns !== undefined) {
        // API returns 1 or 0
        setIsActivity(response.data.retinaDns === 1);
      } else {
        setIsActivity(false);
      }
    } catch (error) {
      console.error('Error fetching Retina DNS visibility:', error);
      setIsActivity(false);
    }
  }

  // Determine device type based on device name or other characteristics
  const getDeviceType = (deviceData: any): string => {
    if (!deviceData) return 'laptop';
    
    const name = deviceData.name?.toLowerCase() || '';
    if (name.includes('mobile') || name.includes('phone') || name.includes('tablet')) return 'mobile';
    if (name.includes('server') || name.includes('database')) return 'server';
    if (name.includes('camera') || name.includes('iot') || name.includes('sensor')) return 'iot';
    return 'laptop';
  };

  const menuList = [
    'Details',
    'Threats',
    'CVE Threats',
    'Metrics',
    'Manage',
    'Mobile Apps',
    'Risk Score',
    ...(isActivity ? ['Activity'] : []), // 👈 dynamically add Activity tab
  ];

  function handleMenuClick(menuName: string) {
    setSelectedMenu(menuName);
  }
  const tabIcons: Record<string, LucideIcon> = {
    Details: Info,
    Threats: ShieldAlert,
    'CVE Threats': Bug,
    Metrics: Activity,
    Manage: SlidersHorizontal,
    'Mobile Apps': Smartphone,
    'Risk Score': Gauge,
    Activity: Globe,
  };
  // Tab ids stay English (used as state); labels are translated at render.
  const tabLabelKeys: Record<string, string> = {
    Details: 'details',
    Threats: 'threats',
    'CVE Threats': 'cveThreats',
    Metrics: 'metrics',
    Manage: 'manage',
    'Mobile Apps': 'mobileApps',
    'Risk Score': 'riskScore',
    Activity: 'activity',
  };

  const handleBackToAccountDetailsPage = () => {
    if (type === 'accountsPage') {
      navigate(`/operators/${operatorId}/cpe/${cpeId}`);
    } else if (type === 'devicesPage') {
      navigate(`/general-list/${operatorId}/operatordevices/Total Devices Managed`);
    }
  };

  const connected = devicesData?.connectionState && devicesData.connectionState !== 'NOT_CONNECTED';

  const renderPanel = () => {
    switch (selectedMenu) {
      case 'Details':
        return <DetailsDevices summaryData={summaryData} />;
      case 'CVE Threats':
        return <CVEThreats isConnected={devicesData?.connectionState} />;
      case 'Metrics':
        return <MetricsDevices summaryData={summaryData} />;
      case 'Manage':
        return <ManageDevices deviceData={devicesData} />;
      case 'Risk Score':
        return <RiskScoreCardGrid deviceData={getSampleDeviceData(getDeviceType(devicesData))} />;
      case 'Threats':
        return <ThreatsByType darkmode={props.darkmode} summaryData={summaryData?.data} />;
      case 'Mobile Apps':
        return <MobileApps deviceData={devicesData} />;
      case 'Activity':
        return isActivity ? <DeviceActivity deviceData={devicesData} darkMode={props.darkmode} /> : null;
      default:
        return null;
    }
  };

  return (
    <div className="bd dd-page" data-theme={props.darkmode ? 'dark' : 'light'} data-accent="mint">
      <section id="cpe-details" className="dd-wrap">
        {backBtnText && (
          <button type="button" className="bd-pill dd-back" onClick={handleBackToAccountDetailsPage}>
            <ArrowLeft size={15} />
            {backBtnText}
          </button>
        )}

        <header className="dd-hd">
          <span className="dd-hd-icon">
            <MonitorSmartphone size={24} strokeWidth={1.75} />
          </span>
          <div className="bd-page-hd dd-hd-text">
            <span className="bd-eyebrow">{t('deviceDetails.page.eyebrow')}</span>
            <h1 className="bd-h1 bd-page-title">
              {devicesData?.name || <span className="dd-title-placeholder">{t('deviceDetails.page.loadingDevice')}</span>}
            </h1>
            <span className="bd-body">{t('deviceDetails.page.description')}</span>
          </div>
        </header>

        {devicesData && (
          <div className="dd-facts">
            <span className={`bd-chip ${connected ? 'mint' : 'danger'}`}>
              <span className="dot" />
              {connected ? t('deviceDetails.status.connected') : t('deviceDetails.status.notConnected')}
            </span>
            {devicesData.deviceType?.key && <span className="bd-chip">{devicesData.deviceType.key}</span>}
            <span className={`bd-chip ${devicesData.cyberShield ? 'mint' : 'warn'}`}>
              {devicesData.cyberShield ? <ShieldCheck size={12} /> : <ShieldOff size={12} />}
              {devicesData.cyberShield ? t('deviceDetails.status.protected') : t('deviceDetails.status.notProtected')}
            </span>
            <span className={`bd-chip ${devicesData.internetAllowed ? '' : 'danger'}`}>
              {devicesData.internetAllowed ? <Wifi size={12} /> : <WifiOff size={12} />}
              {devicesData.internetAllowed ? t('deviceDetails.status.internetAllowed') : t('deviceDetails.status.internetBlocked')}
            </span>
            {devicesData.macAddress && <span className="bd-tag dd-tag">MAC {devicesData.macAddress}</span>}
            {devicesData.ipAddress && <span className="bd-tag dd-tag">IP {devicesData.ipAddress}</span>}
            <span className="bd-tag dd-tag">ID {deviceId}</span>
          </div>
        )}

        {/* Tabs Navigation */}
        <div className="bd-tabbar dd-tabs" role="tablist" aria-label={t('deviceDetails.page.tabsAriaLabel')}>
          {menuList.map((item) => {
            const Icon = tabIcons[item] || Info;
            const active = selectedMenu === item;
            return (
              <button
                key={item}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => handleMenuClick(item)}
                className={`bd-tabbar-item ${active ? 'active' : ''}`}
              >
                <Icon size={16} strokeWidth={1.9} />
                <span>{tabLabelKeys[item] ? t(`deviceDetails.tabs.${tabLabelKeys[item]}`) : item}</span>
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          <div className={`bd-card dd-panel ${selectedMenu === 'Activity' ? 'flush' : ''}`}>{renderPanel()}</div>
        </div>
      </section>
    </div>
  );
};
